import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useEmpresas } from '@/hooks/useEmpresa';
import { useCalendarioGeral, type CalendarioEvento, type CalendarioParticipando } from '@/hooks/useCalendario';
import { CalendarioGrid } from '@/components/calendario/CalendarioGrid';
import { buildGridDays, dateKey } from '@/lib/calendario';
import { VincularEventoModal } from '@/components/calendario/VincularEventoModal';
import { LicitacaoDetalhesModal } from '@/components/gestao-licitacoes/LicitacaoDetalhesModal';
import { CATEGORIAS, UFS_BR } from '@/lib/licitacoesOpcoes';
import { useViewModeStore } from '@/stores/view-mode';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

function Calendario() {
  const empresaFromUrl = parseInt(new URLSearchParams(window.location.search).get('empresa') || '', 10);
  const { mode, previewEmpresaId } = useViewModeStore();
  const empresaEmContexto = mode === 'empresa' && previewEmpresaId !== null
    ? previewEmpresaId
    : (empresaFromUrl || null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [filtrosAbertos, setFiltrosAbertos] = useState(Boolean(empresaEmContexto));
  const [empresaIds, setEmpresaIds] = useState<number[]>(empresaEmContexto ? [empresaEmContexto] : []);
  const [uf, setUf] = useState('');
  const [categoria, setCategoria] = useState('');
  const [modalidade, setModalidade] = useState('');
  const [modalState, setModalState] = useState<{ open: boolean; defaultDate: string; evento: CalendarioEvento | null }>({
    open: false,
    defaultDate: dateKey(now),
    evento: null,
  });
  const [detalhesParticipando, setDetalhesParticipando] = useState<CalendarioParticipando | null>(null);

  useEffect(() => {
    if (!empresaEmContexto) return;
    setEmpresaIds([empresaEmContexto]);
    setFiltrosAbertos(true);
  }, [empresaEmContexto]);

  const empresaIdsEfetivos = empresaEmContexto ? [empresaEmContexto] : empresaIds;

  const { data: empresas } = useEmpresas();

  const gridDays = buildGridDays(year, month);
  const de = dateKey(gridDays[0]);
  const ate = dateKey(gridDays[gridDays.length - 1]);

  const { data: resumo, isLoading } = useCalendarioGeral(de, ate, {
    empresaIds: empresaIdsEfetivos.length > 0 ? empresaIdsEfetivos : undefined,
    uf: uf || undefined,
    categoria: categoria || undefined,
    modalidade: modalidade || undefined,
  });

  const irParaMesAnterior = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); } else { setMonth((m) => m - 1); }
  };

  const irParaProximoMes = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); } else { setMonth((m) => m + 1); }
  };

  const toggleEmpresa = (id: number) => {
    setEmpresaIds((atual) => (atual.includes(id) ? atual.filter((e) => e !== id) : [...atual, id]));
  };

  const limparFiltros = () => {
    setEmpresaIds([]);
    setUf('');
    setCategoria('');
    setModalidade('');
  };

  const temFiltrosAtivos = empresaIdsEfetivos.length > 0 || !!uf || !!categoria || !!modalidade;

  const abrirVincular = (data: string) => {
    setModalState({ open: true, defaultDate: data, evento: null });
  };

  const abrirEditar = (evento: CalendarioEvento) => {
    setModalState({ open: true, defaultDate: evento.dataEvento.slice(0, 10), evento });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary">Calendário de Licitações</h1>
        <p className="text-ds-text-secondary mt-1">
          {empresaEmContexto ? 'Prazos e marcos das licitações da empresa selecionada.' : 'Visão macro de todas as licitações vinculadas e no funil, de todos os clientes.'}
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setFiltrosAbertos((v) => !v)}
            >
              <Filter className="h-4 w-4 mr-1.5" />
              Filtros
              {temFiltrosAtivos && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-ds-success/20 text-ds-success text-[10px] font-bold w-4 h-4">
                  {[empresaIds.length > 0, !!uf, !!categoria, !!modalidade].filter(Boolean).length}
                </span>
              )}
            </Button>

            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={irParaMesAnterior} aria-label="Mês anterior">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium text-ds-text-primary min-w-[140px] text-center">
                {MESES[month]} {year}
              </span>
              <Button type="button" variant="secondary" size="sm" onClick={irParaProximoMes} aria-label="Próximo mês">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {filtrosAbertos && (
            <div className="rounded-lg border border-ds-border bg-ds-surface2/60 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="calendario-filtro-uf">UF</label>
                  <select
                    id="calendario-filtro-uf"
                    className={selectClassName}
                    value={uf}
                    onChange={(e) => setUf(e.target.value)}
                  >
                    <option value="">Todos os estados</option>
                    {UFS_BR.map((sigla) => (
                      <option key={sigla} value={sigla}>{sigla}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="calendario-filtro-categoria">Nicho (categoria)</label>
                  <select
                    id="calendario-filtro-categoria"
                    className={selectClassName}
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                  >
                    <option value="">Todas as categorias</option>
                    {CATEGORIAS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="calendario-filtro-modalidade">Modalidade</label>
                  <input
                    id="calendario-filtro-modalidade"
                    className={selectClassName}
                    placeholder="Ex: Pregão Eletrônico"
                    value={modalidade}
                    onChange={(e) => setModalidade(e.target.value)}
                  />
                </div>
              </div>

              {!empresaEmContexto && empresas && empresas.length > 0 && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary">Clientes / Empresas</label>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {empresas.map((empresa) => (
                      <label
                        key={empresa.id}
                        className="flex items-center gap-1.5 cursor-pointer text-xs text-ds-text-primary bg-ds-surface border border-ds-border rounded-full px-2.5 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={empresaIds.includes(empresa.id)}
                          onChange={() => toggleEmpresa(empresa.id)}
                          className="accent-ds-success w-3.5 h-3.5"
                        />
                        {empresa.razao_social}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {temFiltrosAtivos && (
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={limparFiltros}>
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-ds-text-secondary">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-ds-info" />
              Vinculada (calendário)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-ds-warning" />
              No funil (prazo de abertura)
            </span>
          </div>

          {isLoading ? (
            <p className="text-sm text-ds-text-secondary py-6 text-center">Carregando calendário...</p>
          ) : (
            <CalendarioGrid
              year={year}
              month={month}
              vinculadas={resumo?.vinculadas || []}
              participando={resumo?.participando || []}
              onDiaClick={abrirVincular}
              onEventoClick={abrirEditar}
              onParticipandoClick={setDetalhesParticipando}
            />
          )}
        </CardContent>
      </Card>

      <VincularEventoModal
        isOpen={modalState.open}
        onClose={() => setModalState((prev) => ({ ...prev, open: false }))}
        empresas={empresas || []}
        defaultEmpresaId={empresaIdsEfetivos.length === 1 ? empresaIdsEfetivos[0] : undefined}
        defaultDate={modalState.defaultDate}
        eventoParaEditar={modalState.evento}
      />

      <LicitacaoDetalhesModal entrada={detalhesParticipando} onClose={() => setDetalhesParticipando(null)} />
    </div>
  );
}

export { Calendario };
